import React, { useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Button } from '../../ui/button';
import { Truck, Warehouse } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { doConfirmInactiveOrder, doGetInactiveOrders } from '../../../modules/orders/store/thunkts';
import { AppDispatch } from '@/redux/store';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { selectInactiveOrders } from '../../../modules/orders/store/slice';

const InActiveOrders = ({ role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const inactiveOrders = useSelector(selectInactiveOrders);
  const description =
    role === 'carrier'
      ? 'We expect these orders to be in transit, but did not receive an update from you yet. Please verify that they are with you, or if you have not received them yet.'
      : role === 'merchant'
        ? 'We expect these orders to be in transit, but did not receive an update yet. Please verify that they are with the carrier, or still in your warehouse.'
        : undefined;

  useEffect(() => {
    dispatch(doGetInactiveOrders());
  }, [dispatch]);

  const handleRemoveOrder = (
    orderId: string,
    action: 'CONFIRM_WITH_ME' | 'CONFIRM_NOT_WITH_ME',
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    dispatch(doConfirmInactiveOrder({ orderId, action }));
  };

  const sortedInactiveOrders = useMemo(
    () =>
      [...inactiveOrders]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 10),
    [inactiveOrders],
  );

  const handleRowClick = (orderId: string, e: React.MouseEvent) => {
    const directTo = `/orders/${orderId}`;
    if (e.ctrlKey || e.metaKey) {
      window.open(directTo, '_blank');
    } else {
      history.push(directTo);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ scale: 1 }} animate={{ scale: 1 }} transition={{ duration: 0.8, ease: 'easeInOut' }}>
        <Card className="min-h-[400px]">
          <CardHeader>
            <CardTitle>Inactive orders</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Order ID</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Delivery Address</TableHead>
                  <TableHead className="text-right">Verify</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactiveOrders?.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                      No inactive orders that need your attention, enjoy your day!
                    </TableCell>
                  </TableRow>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {sortedInactiveOrders.map((order) => (
                      <motion.tr
                        key={order.orderId}
                        initial={{ opacity: 1, height: 'auto' }}
                        exit={{
                          opacity: 0,
                          height: 0,
                          transition: {
                            duration: 0.2,
                            ease: 'easeOut',
                          },
                        }}
                        onClick={(e) => handleRowClick(order.orderId, e)}
                        className="hover:cursor-pointer hover:bg-gray-50"
                        style={{ display: 'table-row' }}
                        layout
                      >
                        <TableCell className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                          <div>{order.orderId}</div>
                        </TableCell>
                        <TableCell className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                          <div>{order.orderRef}</div>
                        </TableCell>
                        <TableCell
                          title={order.deliveryAddress}
                          className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                        >
                          <div>{order.deliveryAddress}</div>
                        </TableCell>
                        <TableCell className="text-right overflow-hidden text-ellipsis whitespace-nowrap">
                          <div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mr-1"
                                    onClick={(e) => handleRemoveOrder(order.orderId, 'CONFIRM_NOT_WITH_ME', e)}
                                  >
                                    {role === 'carrier' ? <Warehouse /> : role === 'merchant' ? <Truck /> : undefined}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Order is not with you</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => handleRemoveOrder(order.orderId, 'CONFIRM_WITH_ME', e)}
                                  >
                                    {role === 'carrier' ? <Truck /> : role === 'merchant' ? <Warehouse /> : undefined}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Order is with you</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                )}
              </TableBody>
              {inactiveOrders?.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3}>Total Inactive orders</TableCell>
                    <TableCell className="text-right">{inactiveOrders?.length}</TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default InActiveOrders;
